'use client';

import {
  Github,
  Linkedin,
  Instagram,
  Facebook,
  MessageCircle,
  Mail,
  ArrowUp,
  Download,
  Eye,
} from 'lucide-react';
import VisitorBadge from './VisitorBadge';
import { useCVModal } from '../context/CVModalContext';

const NAV_LINKS = [
  { href: '#about', label: 'About' },
  { href: '#skills', label: 'Skills' },
  { href: '#experience', label: 'Experience' },
  { href: '#projects', label: 'Projects' },
  { href: '#education', label: 'Education' },
  { href: '#github', label: 'GitHub' },
  { href: '#contact', label: 'Contact' },
];

const SOCIAL_LINKS = [
  { icon: Github, href: 'https://github.com/Marwan-Hussein', label: 'GitHub' },
  { icon: Linkedin, href: 'https://www.linkedin.com/in/marwanhussein9', label: 'LinkedIn' },
  { icon: MessageCircle, href: 'https://wa.me/qr/BQP37VAJ5RB4J1', label: 'WhatsApp' },
  { icon: Instagram, href: 'https://www.instagram.com/marwan_hussein9?igsh=MWNzejU5em9hZDIyYw==', label: 'Instagram' },
  { icon: Facebook, href: 'https://www.facebook.com/share/1JALCAMUu4/', label: 'Facebook' },
  { icon: Mail, href: 'mailto:marwan.hussein.450@gmail.com', label: 'Email' },
];

export default function FooterSection() {
  const { openCVModal } = useCVModal();
  const currentYear = new Date().getFullYear();

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleNavClick = (href: string) => {
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const handleDownloadCV = () => {
    const link = document.createElement('a');
    link.href = '/assets/documents/Marwan-Hussein-CV.pdf';
    link.download = 'Marwan-Hussein-CV.pdf';
    link.click();
  };

  return (
    <footer className="relative border-t border-parchment/6 bg-surface/40">
      <div className="h-px w-full bg-gradient-to-r from-transparent via-sand/25 to-transparent" />

      <div className="max-w-screen-2xl mx-auto px-6 lg:px-10 xl:px-16 py-14 xl:py-16">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 mb-12">
          <div className="md:col-span-5">
            <span className="font-display text-2xl font-700 text-parchment mb-4 block">
              Marwan<span className="text-sand">.</span>Hussein
            </span>
            <p className="text-parchment-muted text-sm leading-relaxed mb-6 max-w-sm">
              Software engineer crafting thoughtful, production-ready digital experiences.
              Open to full-time roles and freelance projects.
            </p>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={openCVModal}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-parchment/10 text-parchment-muted text-xs font-display hover:border-sand/25 hover:text-sand transition-all duration-300"
              >
                <Eye size={13} />
                CV
              </button>
              <button
                type="button"
                onClick={handleDownloadCV}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-sand/25 bg-sand/8 text-sand text-xs font-display hover:bg-sand/12 transition-all duration-300"
              >
                <Download size={13} />
                Download
              </button>
            </div>
          </div>

          <div className="md:col-span-3 md:pl-8">
            <h4 className="font-mono text-[10px] text-parchment-muted uppercase tracking-widest mb-4">
              Navigate
            </h4>
            <ul className="space-y-2">
              {NAV_LINKS.map((link) => (
                <li key={link.href}>
                  <button
                    type="button"
                    onClick={() => handleNavClick(link.href)}
                    className="link-hover text-parchment-muted text-sm hover:text-sand transition-colors duration-300"
                  >
                    {link.label}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          <div className="md:col-span-4">
            <h4 className="font-mono text-[10px] text-parchment-muted uppercase tracking-widest mb-4">
              Get in touch
            </h4>
            <a
              href="mailto:marwan.hussein.450@gmail.com"
              className="link-hover inline-flex items-center gap-2 text-parchment-muted text-sm hover:text-sand transition-colors duration-300 mb-5"
            >
              <Mail size={13} />
              marwan.hussein.450@gmail.com
            </a>
            <div className="flex items-center gap-2 flex-wrap">
              {SOCIAL_LINKS.map((social) => {
                const Icon = social.icon;
                return (
                  <a
                    key={social.label}
                    href={social.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={social.label}
                    className="p-2.5 rounded-lg border border-parchment/8 text-parchment-muted hover:text-sand hover:border-sand/25 hover:bg-sand/5 transition-all duration-300"
                  >
                    <Icon size={15} />
                  </a>
                );
              })}
            </div>
          </div>
        </div>

        <div className="pt-8 border-t border-parchment/6 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex flex-col sm:flex-row items-center gap-4">
            <p className="text-parchment-muted/50 text-xs font-mono">
              © {currentYear} Marwan Hussein
            </p>
            <VisitorBadge />
          </div>
          <button
            type="button"
            onClick={scrollToTop}
            className="flex items-center gap-2 text-parchment-muted text-xs hover:text-sand transition-colors duration-300 group"
            aria-label="Scroll to top"
          >
            <span>Back to top</span>
            <span className="p-1.5 rounded border border-parchment/10 group-hover:border-sand/25 transition-all duration-300">
              <ArrowUp size={12} />
            </span>
          </button>
        </div>
      </div>
    </footer>
  );
}
